document.addEventListener('alpine:init', () => {
    Alpine.data('cartUpgrade', (item, options = {}) => ({
        item,
        isUpdating: false,
        upgradeError: '',
        upgradeErrorTimeout: null,
        errorMessage: options.errorMessage || 'Unable to upgrade this item. Please try again.',
        limitMessage: options.limitMessage || 'This item is limited to 3 per order.',
        quarterlyLimitMessage: options.quarterlyLimitMessage || 'This item is limited to 1 per customer.',

        /**
         * Initializes the cart upgrade component.
         * @returns {void}
         */
        init() {
            // Re-sync this line from the store whenever the displayed cart
            // changes from anything other than our own in-flight request
            // (qty changes, GWP add/remove, external add-to-cart, etc.).
            // Watching `displayData` keeps the bound item consistent with
            // what is currently rendered.
            this.$watch('$store.cart.displayData', () => {
                if (this.isUpdating) return;
                this.syncFromStore();
            });
        },

        /**
         * Clears the upgrade error message and any pending auto-clear timer.
         * @returns {void}
         */
        clearUpgradeError() {
            if (this.upgradeErrorTimeout) {
                clearTimeout(this.upgradeErrorTimeout);
                this.upgradeErrorTimeout = null;
            }
            this.upgradeError = '';
        },

        /**
         * Sets the upgrade error message and auto-clears it after 5 seconds.
         * @param {string} message
         * @returns {void}
         */
        setUpgradeError(message) {
            this.clearUpgradeError();
            if (!message) return;
            this.upgradeError = message;
            this.upgradeErrorTimeout = setTimeout(() => {
                this.upgradeError = '';
                this.upgradeErrorTimeout = null;
            }, 5000);
        },

        /**
         * Re-pulls this line from the cart store and resets the bound item.
         * @returns {void}
         */
        syncFromStore() {
            // Read from `displayItems`, not `items`: the drawer renders grouped
            // rows and the group metadata has to survive a resync, or an upgrade
            // fired afterwards would only move one of the grouped lines.
            const updated = Alpine.store('cart').displayItems.find((i) => i.key === this.item.key);
            if (!updated) return;
            this.item = updated;
        },

        /**
         * Every real cart line key this card's line stands for. Lines that
         * Shopify split apart by discount entitlement are rendered as one row,
         * so an upgrade has to move all of them, not just the entitled one.
         * @returns {string[]}
         */
        get groupKeys() {
            const keys = this.item?._groupKeys;
            return Array.isArray(keys) && keys.length ? keys : [this.item?.key].filter(Boolean);
        },

        /**
         * Total quantity across every line this card's row stands for.
         * @returns {number}
         */
        get groupQuantity() {
            return Number(this.item?._groupQuantity ?? this.item?.quantity) || 0;
        },

        /* ============ Eligibility ============ */

        /**
         * True if the cart line is a managed GWP line.
         * @returns {boolean}
         */
        get isFreeGift() {
            return this.item?.properties?._isFreeGift === 'true';
        },

        /**
         * True if a cart line has a "Free Gift" discount allocation.
         * Shopify may attach this to only the first entitled subscription line
         * when the same product is split across multiple lines.
         * @param {Object} item
         * @returns {boolean}
         */
        hasFreeGiftDiscount(item) {
            if (!item) return false;

            const discounts = item.discounts || [];
            if (discounts.some((d) => d?.title === 'Free Gift')) return true;

            const allocations = item.line_level_discount_allocations || [];
            return allocations.some(
                (a) => a?.discount_application?.title === 'Free Gift'
            );
        },

        /**
         * Base quarterly upgrade eligibility for a cart line (no dedupe).
         * @param {Object} item
         * @returns {boolean}
         */
        isQuarterlyEligible(item) {
            if (!item) return false;
            if (!item.selling_plan_allocation) return false;
            if (item.properties?._isFreeGift === 'true') return false;
            const props = item.properties || {};
            return Boolean(props._quarterly_variant_id);
        },

        /**
         * Monthly card eligibility:
         *  - line is currently OTP (no selling plan)
         *  - product page provided a `_subscription_product` handle
         *  - product page provided a `_subscription_selling_plan` id
         * @returns {boolean}
         */
        get canUpgradeMonthly() {
            const variantsToHide = [48747862327548, 44151331651836, 46921688809724, 48747862294780, 44151331684604, 46921688842492];

            if (variantsToHide.includes(this.item.variant_id)) return false;
            if (!this.item) return false;
            if (this.item.selling_plan_allocation) return false;
            if (this.isFreeGift) return false;
            const props = this.item.properties || {};
            return Boolean(props._subscription_product && props._subscription_selling_plan);
        },

        /**
         * Quarterly card eligibility:
         *  - line is currently a subscription
         *  - product page provided a `_quarterly_variant_id`
         *  - shown only once per product_id; prefers the line with a Free Gift discount
         * @returns {boolean}
         */
        get canUpgradeQuarterly() {
            if (!this.isQuarterlyEligible(this.item)) return false;

            const cart = Alpine.store('cart');
            if (this.groupQuantity > cart.getQuarterlyRemainingQty()) return false;

            const productId = Number(this.item.product_id);
            const siblings = (cart.items || []).filter(
                (i) => Number(i.product_id) === productId && this.isQuarterlyEligible(i)
            );
            if (!siblings.length) return false;

            const withGift = siblings.filter((i) => this.hasFreeGiftDiscount(i));
            const primary = withGift.length ? withGift[0] : siblings[0];
            return primary.key === this.item.key;
        },

        /* ============ Pricing helpers (all in cents) ============ */

        /**
         * Unit price shown on the cart row (cents).
         * Display math must use this — `_subscription_price` / `_quarterly_price`
         * are linked-variant hints and are often already ~0.5× the OTP row price
         * (e.g. $39.50 vs $78.99), so discounting them again understates the offer.
         * @returns {number}
         */
        get cartRowUnitPrice() {
            const unit = Number(this.item?.final_price ?? this.item?.price);
            return Number.isFinite(unit) && unit > 0 ? unit : 0;
        },

        /**
         * Subscription linked-variant hint (cents). Fallback only — not for
         * primary display math.
         * @returns {number}
         */
        get monthlyBasePrice() {
            const subProp = Number(this.item?.properties?._subscription_price);
            if (Number.isFinite(subProp) && subProp > 0) return subProp;

            const allocPrice = this.item?.selling_plan_allocation?.price;
            if (Number.isFinite(allocPrice) && allocPrice > 0) return allocPrice;

            return this.cartRowUnitPrice || this.item?.price || 0;
        },

        /**
         * OTP list price for the monthly upgrade card (cents).
         * Prefer compare/original over final_price so cart discounts (e.g. $39.50)
         * are not treated as the OTP base ($78.99).
         * @returns {number}
         */
        get monthlyComparePrice() {
            const subCompare = Number(this.item?.properties?._subscription_compare_at_price);
            if (Number.isFinite(subCompare) && subCompare > 0) return Math.round(subCompare);

            const original = Number(this.item?.original_price);
            const price = Number(this.item?.price);
            const finalPrice = this.cartRowUnitPrice;
            const best = Math.max(original || 0, price || 0, finalPrice || 0);
            if (best > 0) return best;

            return this.monthlyBasePrice;
        },

        /**
         * Monthly sale: 40% off OTP (e.g. $78.99 → $47.39).
         * Uses `_subscription_price` when it is already ~40% off; never discounts
         * a reduced cart final_price again ($39.50 × 0.6 = $23.70).
         * @returns {number}
         */
        get monthlySalePrice() {
            const otp = this.monthlyComparePrice;
            if (!otp) return 0;

            const subProp = Number(this.item?.properties?._subscription_price);
            if (Number.isFinite(subProp) && subProp > 0) {
                const ratio = subProp / otp;
                // Hint already looks like ~40% off list — show as-is.
                if (ratio >= 0.55 && ratio <= 0.7) return Math.round(subProp);
            }

            return Math.round(otp * 0.6);
        },

        /**
         * Quarterly "billed every 12 weeks" ("Save 40% for Life"): always
         * OTP × 3 × 0.6 (e.g. $78.99 → $142.18).
         * `_quarterly_price` may be either the 3-month list (~$236.97) or a
         * ~50% allocation (~$118.50) — never assume one without checking.
         * @returns {number}
         */
        get quarterlyBilledPrice() {
            const lifeOff = 0.6;
            const props = this.item?.properties || {};
            const qCompare = Number(props._quarterly_compare_at_price);
            const qPrice = Number(props._quarterly_price);
            const row = this.cartRowUnitPrice;

            const fromOtp = (otpCents) => {
                const otp = Number(otpCents);
                if (!Number.isFinite(otp) || otp <= 0) return null;
                return Math.round(otp * 3 * lifeOff);
            };

            // Unit OTP from monthly compare / cart original.
            const subCompare = Number(props._subscription_compare_at_price);
            if (Number.isFinite(subCompare) && subCompare > 0) {
                return fromOtp(subCompare);
            }

            const original = Number(this.item?.original_price);
            if (Number.isFinite(original) && original > row) {
                const billed = fromOtp(original);
                if (billed != null) return billed;
            }

            // 3-month list → ×0.6 (same as OTP×3×0.6).
            if (Number.isFinite(qCompare) && qCompare > 0 && qCompare > (qPrice || 0)) {
                return Math.round(qCompare * lifeOff);
            }

            if (Number.isFinite(qPrice) && qPrice > 0) {
                // List (~3×OTP) vs allocation (~0.5× list).
                const looksLikeList =
                    (Number.isFinite(qCompare) && qCompare > 0 && qPrice >= qCompare * 0.9) ||
                    (row > 0 && qPrice >= row * 4);
                if (looksLikeList) {
                    return Math.round(qPrice * lifeOff);
                }
                // Allocation at ~50% off list → OTP = (qPrice / 0.5) / 3.
                return fromOtp(qPrice / 0.5 / 3);
            }

            // Undo rounded monthly sale (ceil so $47.39 → $78.99).
            if (row > 0) {
                return fromOtp(Math.ceil(row / lifeOff - 1e-9));
            }

            return this.item?.price || 0;
        },

        /**
         * Resolves the quarterly offer image URL.
         * Falls back to the default capsule artwork.
         * @returns {string}
         */
        get quarterlyImage() {
            const raw = this.item?.properties?._quarterly_image;
            if (!raw) {
                return 'https://cdn.shopify.com/s/files/1/0606/9773/9516/files/RebuyQuarter.webp?v=1770707070';
            }
            return raw.startsWith('//') ? `https:${raw}` : raw;
        },

        /* ============ Actions ============ */

        /**
         * OTP -> Subscription upgrade.
         * Adds the linked subscription variant first, then removes the OTP line.
         * @returns {Promise<void>}
         */
        async upgradeToMonthly() {
            if (this.isUpdating || !this.canUpgradeMonthly) return;

            const props = this.item.properties || {};
            const handle = props._subscription_product;
            const sellingPlanId = props._subscription_selling_plan;
            if (!handle || !sellingPlanId) return;

            this.isUpdating = true;
            this.clearUpgradeError();
            const cart = Alpine.store('cart');
            const sourceLine = {
                keys: this.groupKeys,
                key: this.item.key,
                quantity: this.groupQuantity,
                variantId: this.item.variant_id,
                productId: this.item.product_id,
                preferFreeGift: this.hasFreeGiftDiscount(this.item),
                sourceHasSellingPlan: Boolean(this.item.selling_plan_allocation),
            };
            const oldQty = sourceLine.quantity;

            cart.beginOp();

            try {
                const product = await this.fetchProductByHandle(handle);
                const variant = this.findMatchingVariant(product, this.item);
                if (!variant) throw new Error('No matching variant for subscription upgrade');

                const existingSubQty = cart.getSubscriptionProductQty(product.id);
                if (existingSubQty + oldQty > cart.subscriptionMaxQty) {
                    this.setUpgradeError(this.limitMessage);
                    return;
                }

                const newProps = this.stripProperties(this.item.properties, [
                    '_subscription_product',
                    '_subscription_selling_plan',
                ]);
                newProps.upgraded_in_cart_monthly = 'true';

                await cart.addItem({
                    id: variant.id,
                    quantity: oldQty,
                    properties: newProps,
                    selling_plan: sellingPlanId,
                    product_id: product.id,
                });

                await this.removeUpgradedSourceLine(sourceLine);
                this.clearUpgradeError();
            } catch (error) {
                console.error('[cartUpgrade] monthly upgrade failed', error);
                this.setUpgradeError(this.resolveUpgradeError(error));
            } finally {
                this.isUpdating = false;
                cart.endOp();
            }
        },

        /**
         * Monthly Subscription -> Quarterly Subscription upgrade.
         * Adds the quarterly variant + selling plan first, then removes the
         * monthly line.
         * @returns {Promise<void>}
         */
        async upgradeToQuarterly() {
            if (this.isUpdating || !this.canUpgradeQuarterly) return;

            const props = this.item.properties || {};
            const variantId = parseInt(props._quarterly_variant_id, 10);
            const productHandle = props._quarterly_product_handle;
            let sellingPlanId = props._quarterly_selling_plan_id;
            if (!variantId) return;

            this.isUpdating = true;
            this.clearUpgradeError();
            const cart = Alpine.store('cart');
            const sourceLine = {
                keys: this.groupKeys,
                key: this.item.key,
                quantity: this.groupQuantity,
                variantId: this.item.variant_id,
                productId: this.item.product_id,
                preferFreeGift: this.hasFreeGiftDiscount(this.item),
                sourceHasSellingPlan: Boolean(this.item.selling_plan_allocation),
            };
            const oldKeys = sourceLine.keys;
            const oldQty = sourceLine.quantity;

            cart.beginOp();

            try {
                let targetProductId = null;
                let product = null;

                if (productHandle) {
                    product = await this.fetchProductByHandle(productHandle);
                    targetProductId = product?.id || null;
                    if (!this.isValidId(sellingPlanId)) {
                        sellingPlanId = this.findSellingPlanId(product);
                    }
                }

                if (!targetProductId) {
                    const existingLine = cart.items.find((i) => Number(i.variant_id) === variantId);
                    targetProductId = existingLine?.product_id || null;
                }

                if (targetProductId) {
                    const remainingQuarterly = cart.getQuarterlyRemainingQty();
                    if (oldQty > remainingQuarterly) {
                        this.setUpgradeError(this.quarterlyLimitMessage);
                        return;
                    }
                }

                await cart.addItem({
                    id: variantId,
                    quantity: oldQty,
                    properties: { upgraded_in_cart_quarterly: 'true' },
                    selling_plan: this.isValidId(sellingPlanId) ? parseInt(sellingPlanId, 10) : undefined,
                    product_id: targetProductId,
                });

                await this.removeUpgradedSourceLine(sourceLine);
                this.clearUpgradeError();
            } catch (error) {
                console.error('[cartUpgrade] quarterly upgrade failed', error);
                this.setUpgradeError(this.resolveUpgradeError(error));
            } finally {
                this.isUpdating = false;
                cart.endOp();
            }
        },

        /* ============ Internal helpers ============ */

        /**
         * Removes only the upgraded source quantity after an upgrade add.
         * Re-resolves the line because Free Gift discounts can change keys or
         * merge sibling lines during the add. Subtracts `quantity` instead of
         * zeroing a possibly-merged line.
         * @param {Object} source
         * @param {string} source.key
         * @param {number} source.quantity
         * @param {number|string} source.variantId
         * @param {number|string} source.productId
         * @param {boolean} [source.preferFreeGift=false]
         * @param {boolean} [source.sourceHasSellingPlan=false]
         * @returns {Promise<void>}
         */
        async removeUpgradedSourceLine({
            keys,
            key,
            quantity,
            variantId,
            productId,
            preferFreeGift = false,
            sourceHasSellingPlan = false,
        } = {}) {
            const cart = Alpine.store('cart');
            let qtyToRemove = Math.max(0, Math.floor(Number(quantity) || 0));
            if (!qtyToRemove) return;

            // The upgraded row may stand for several real lines (Shopify splits
            // on discount entitlement), so drain the quantity across all of
            // them — removing only the first would strand the rest on the old
            // product while the full quantity was added to the new one.
            const keyList = (Array.isArray(keys) && keys.length ? keys : [key]).filter(Boolean).map(String);
            let targets = keyList
                .map((k) => (cart.items || []).find((i) => String(i.key) === k))
                .filter(Boolean);

            if (!targets.length) {
                const candidates = (cart.items || []).filter((i) => {
                    if (Number(i.variant_id) !== Number(variantId)) return false;
                    if (Number(i.product_id) !== Number(productId)) return false;
                    if (i.properties?._isFreeGift === 'true') return false;
                    const hasPlan = Boolean(i.selling_plan_allocation);
                    return sourceHasSellingPlan ? hasPlan : !hasPlan;
                });

                targets = preferFreeGift
                    ? [...candidates].sort((a, b) =>
                        Number(this.hasFreeGiftDiscount(b)) - Number(this.hasFreeGiftDiscount(a)))
                    : candidates;
            }

            if (!targets.length) return;

            const updates = {};
            targets.forEach((line) => {
                if (qtyToRemove <= 0) return;
                const lineQty = Math.max(0, Number(line.quantity) || 0);
                const take = Math.min(lineQty, qtyToRemove);
                qtyToRemove -= take;
                updates[line.key] = lineQty - take;
            });

            await cart.updateLineQuantities(updates);
        },

        /**
         * Extracts a user-facing message from a cart upgrade failure.
         * Prefers Shopify `/cart/add.js` error payloads, then Error.message.
         * @param {Error|Object|string|null} error
         * @returns {string}
         */
        resolveUpgradeError(error) {
            if (!error) return this.errorMessage;

            if (error.code === 'SUBSCRIPTION_QTY_LIMIT') {
                return /1 per/.test(String(error.message || ''))
                    ? this.quarterlyLimitMessage
                    : this.limitMessage;
            }

            const shopifyResponse = error.shopifyResponse;
            if (shopifyResponse) {
                return shopifyResponse.description
                    || shopifyResponse.message
                    || this.errorMessage;
            }

            if (typeof error === 'string') return error;

            return error.message || this.errorMessage;
        },

        /**
         * Returns true if the given value looks like a usable Shopify id.
         * @param {*} value
         * @returns {boolean}
         */
        isValidId(value) {
            if (value === null || value === undefined) return false;
            if (value === '' || value === 'null' || value === 'undefined') return false;
            const num = Number(value);
            return Number.isFinite(num) && num > 0;
        },

        /**
         * Fetches a Shopify product by handle.
         * @param {string} handle
         * @returns {Promise<Object>}
         */
        fetchProductByHandle(handle) {
            return fetch(`/products/${encodeURIComponent(handle)}.js`, {
                credentials: 'same-origin',
            }).then((res) => {
                if (!res.ok) throw new Error(`Failed to fetch product ${handle}`);
                return res.json();
            });
        },

        /**
         * Finds the variant on a fetched product that matches the current
         * cart line. Tries variant_title first, then options-by-position.
         * @param {Object} product
         * @param {Object} cartItem
         * @returns {Object|null}
         */
        findMatchingVariant(product, cartItem) {
            if (!product || !Array.isArray(product.variants)) return null;

            const currentTitle = cartItem.variant_title;
            const optionValues = (cartItem.options_with_values || []).map((o) => o.value);

            let match = product.variants.find((v) => v.title === currentTitle);
            if (match) return match;

            if (optionValues.length) {
                match = product.variants.find((v) => {
                    const opts = [v.option1, v.option2, v.option3].filter(Boolean);
                    if (opts.length !== optionValues.length) return false;
                    return opts.every((o, i) => o === optionValues[i]);
                });
                if (match) return match;
            }

            return null;
        },

        /**
         * Picks a selling plan id from a fetched product, preferring the
         * Skio selling plan group, then the first available group.
         * @param {Object} product
         * @returns {number|null}
         */
        findSellingPlanId(product) {
            if (!product || !Array.isArray(product.selling_plan_groups)) return null;

            const skioGroup = product.selling_plan_groups.find(
                (g) => (g.app_id || '').toLowerCase() === 'skio'
            );
            const groupToUse = skioGroup || product.selling_plan_groups[0];
            if (!groupToUse || !groupToUse.selling_plans?.length) return null;
            return groupToUse.selling_plans[0].id;
        },

        /**
         * Returns a shallow copy of properties with the given keys removed.
         * Handles both object and array (legacy) property shapes.
         * @param {Object|Array} props
         * @param {string[]} ignoreKeys
         * @returns {Object}
         */
        stripProperties(props, ignoreKeys = []) {
            const out = {};
            if (!props) return out;

            if (Array.isArray(props)) {
                props.forEach((p) => {
                    if (!p) return;
                    const key = p.key || p.name;
                    if (!key || ignoreKeys.includes(key)) return;
                    out[key] = p.value;
                });
                return out;
            }

            Object.keys(props).forEach((key) => {
                if (ignoreKeys.includes(key)) return;
                out[key] = props[key];
            });
            return out;
        },
    }));
});
